/**
 * Insights / Dashboard sales analytics — shared across Counter admin frontends.
 * Uses GET /api/sales/by-product and GET /api/sales/timeseries.
 */

export const TIME_PERIODS = {
  LAST_7_DAYS: 'last_7_days',
  LAST_MONTH: 'last_month',
  THIS_MONTH: 'this_month',
  LAST_YEAR: 'last_year',
  THIS_YEAR: 'this_year',
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** datetime-local compatible string for API (local time). */
export function toApiDateTime(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** Date-only string YYYY-MM-DD */
export function toApiDate(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function getPeriodDateRange(timePeriod) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let startDate = new Date(today);
  let endDate = new Date(now);

  switch (timePeriod) {
    case TIME_PERIODS.LAST_7_DAYS:
      startDate.setDate(startDate.getDate() - 6);
      break;
    case TIME_PERIODS.LAST_MONTH: {
      const lastMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
      const lastMonthYear =
        today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
      startDate = new Date(lastMonthYear, lastMonth, 1);
      endDate = new Date(lastMonthYear, lastMonth + 1, 0, 23, 59, 59);
      break;
    }
    case TIME_PERIODS.THIS_MONTH:
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case TIME_PERIODS.LAST_YEAR: {
      const lastYear = today.getFullYear() - 1;
      startDate = new Date(lastYear, 0, 1);
      endDate = new Date(lastYear, 11, 31, 23, 59, 59);
      break;
    }
    case TIME_PERIODS.THIS_YEAR:
      startDate = new Date(today.getFullYear(), 0, 1);
      break;
    default:
      startDate.setDate(startDate.getDate() - 6);
  }

  return {
    startDate,
    endDate,
    start_date: toApiDateTime(startDate),
    end_date: toApiDateTime(endDate),
  };
}

export function getPreviousPeriodDateRange(timePeriod) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (timePeriod === TIME_PERIODS.THIS_YEAR) {
    const y = today.getFullYear() - 1;
    return {
      start_date: toApiDateTime(new Date(y, 0, 1)),
      end_date: toApiDateTime(new Date(y, 11, 31, 23, 59, 59)),
    };
  }
  if (timePeriod === TIME_PERIODS.LAST_YEAR) {
    const y = today.getFullYear() - 2;
    return {
      start_date: toApiDateTime(new Date(y, 0, 1)),
      end_date: toApiDateTime(new Date(y, 11, 31, 23, 59, 59)),
    };
  }
  if (timePeriod === TIME_PERIODS.THIS_MONTH) {
    const m = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    const y = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    return {
      start_date: toApiDateTime(new Date(y, m, 1)),
      end_date: toApiDateTime(new Date(y, m + 1, 0, 23, 59, 59)),
    };
  }
  if (timePeriod === TIME_PERIODS.LAST_MONTH) {
    const cur = today.getMonth() === 0 ? 11 : today.getMonth() - 1;
    const y = cur === 11 && today.getMonth() === 0 ? today.getFullYear() - 1 : today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const prev = cur === 0 ? 11 : cur - 1;
    const py = prev === 11 && cur === 0 ? y - 1 : y;
    return {
      start_date: toApiDateTime(new Date(py, prev, 1)),
      end_date: toApiDateTime(new Date(py, prev + 1, 0, 23, 59, 59)),
    };
  }
  const end = new Date(today);
  end.setDate(end.getDate() - 7);
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  return { start_date: toApiDateTime(start), end_date: toApiDateTime(end) };
}

export function getTodayDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return { start_date: toApiDateTime(start), end_date: toApiDateTime(now) };
}

function timeSeriesGroupBy(timePeriod) {
  if (timePeriod === TIME_PERIODS.THIS_YEAR || timePeriod === TIME_PERIODS.LAST_YEAR) {
    return 'month';
  }
  if (timePeriod === TIME_PERIODS.THIS_MONTH || timePeriod === TIME_PERIODS.LAST_MONTH) {
    return 'week';
  }
  return 'day';
}

function normalizeSalesResponse(res) {
  const data = Array.isArray(res?.data) ? res.data : [];
  const totals = res?.totals || {
    revenue: data.reduce((s, r) => s + parseFloat(r.revenue || 0), 0),
    profit: data.reduce((s, r) => s + parseFloat(r.profit || 0), 0),
    sold_quantity: data.reduce((s, r) => s + parseFloat(r.sold_quantity || 0), 0),
    product_count: data.length,
  };
  return { data, totals, meta: res?.meta || {} };
}

export async function fetchProductSalesForRange(apiService, range, extraParams = {}) {
  const pageSize = 5000;
  let offset = 0;
  let allRows = [];
  let totals = null;
  let meta = {};

  for (;;) {
    const res = await apiService.getProductSales({
      ...extraParams,
      start_date: range.start_date,
      end_date: range.end_date,
      limit: pageSize,
      offset,
    });
    const { data, totals: t, meta: m } = normalizeSalesResponse(res);
    if (totals == null && t) totals = t;
    meta = m || {};
    allRows = allRows.concat(data);
    if (!m?.truncated || data.length === 0) break;
    offset += data.length;
  }

  if (!totals) {
    totals = {
      revenue: allRows.reduce((s, r) => s + parseFloat(r.revenue || 0), 0),
      profit: allRows.reduce((s, r) => s + parseFloat(r.profit || 0), 0),
      sold_quantity: allRows.reduce((s, r) => s + parseFloat(r.sold_quantity || 0), 0),
      product_count: allRows.length,
    };
  }

  return { rows: allRows, totals, meta };
}

export async function fetchSalesTotalsForRange(apiService, range) {
  const res = await apiService.getProductSales({
    start_date: range.start_date,
    end_date: range.end_date,
    limit: 1,
    offset: 0,
  });
  const { totals } = normalizeSalesResponse(res);
  return totals;
}

function fillTimeSeriesBuckets(timePeriod, rawSeries, chartLocale, t) {
  const groupBy = timeSeriesGroupBy(timePeriod);
  const { startDate } = getPeriodDateRange(timePeriod);
  const buckets = [];

  if (groupBy === 'month') {
    const year = startDate.getFullYear();
    for (let m = 0; m < 12; m++) {
      const d = new Date(year, m, 1);
      const match = (rawSeries || []).find((r) => {
        const rd = new Date(r.period || r.name);
        return rd.getFullYear() === year && rd.getMonth() === m;
      });
      buckets.push({
        name: d.toLocaleDateString(chartLocale, { month: 'short' }),
        sales: match ? parseFloat(match.sales || 0) : 0,
        revenue: match ? parseFloat(match.profit || match.revenue || 0) : 0,
      });
    }
    return buckets;
  }

  if (groupBy === 'week') {
    const sorted = [...(rawSeries || [])].sort(
      (a, b) => new Date(a.period || a.name) - new Date(b.period || b.name)
    );
    const weeks = Math.max(
      sorted.length,
      Math.ceil(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate() / 7)
    );
    for (let i = 0; i < weeks; i++) {
      const match = sorted[i];
      buckets.push({
        name: `${t('common.week')} ${i + 1}`,
        sales: match ? parseFloat(match.sales || 0) : 0,
        revenue: match ? parseFloat(match.profit || match.revenue || 0) : 0,
      });
    }
    return buckets;
  }

  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);
    const dateKey = toApiDate(date);
    const match = (rawSeries || []).find((r) => {
      const rd = new Date(r.period || r.name);
      return toApiDate(rd) === dateKey;
    });
    buckets.push({
      name: date.toLocaleDateString(chartLocale, { weekday: 'short' }),
      dateKey,
      sales: match ? parseFloat(match.sales || 0) : 0,
      revenue: match ? parseFloat(match.profit || match.revenue || 0) : 0,
    });
  }
  return buckets;
}

export async function fetchSalesTimeSeries(apiService, timePeriod, chartLocale, t) {
  const range = getPeriodDateRange(timePeriod);
  const group_by = timeSeriesGroupBy(timePeriod);

  const res = await apiService.getSalesTimeSeries({
    start_date: range.start_date,
    end_date: range.end_date,
    group_by,
  });

  const raw = Array.isArray(res?.data) ? res.data : [];
  const totals = res?.totals || {
    revenue: raw.reduce((s, r) => s + parseFloat(r.sales || 0), 0),
    profit: raw.reduce((s, r) => s + parseFloat(r.profit || 0), 0),
  };

  const breakdown = fillTimeSeriesBuckets(timePeriod, raw, chartLocale, t);

  return {
    breakdown,
    totals: {
      sales: parseFloat(totals.revenue ?? totals.sales ?? 0),
      profit: parseFloat(totals.profit ?? 0),
    },
    raw,
  };
}

export function getTopSoldProducts(rows, limit = 10) {
  const sorted = [...(rows || [])]
    .sort((a, b) => parseFloat(b.sold_quantity || 0) - parseFloat(a.sold_quantity || 0))
    .slice(0, limit);

  return sorted.map((p) => ({
    name: String(p.product_name || 'Unknown').replace(/\s+/g, ' ').trim(),
    quantity: parseFloat(p.sold_quantity || 0),
    revenue: parseFloat(p.revenue || 0),
    product_id: p.product_id,
  }));
}

export async function fetchAllOrdersForRange(apiService, range) {
  const pageSize = 2000;
  let offset = 0;
  let all = [];

  for (;;) {
    const res = await apiService.getOrders({
      start_date: range.start_date,
      end_date: range.end_date,
      limit: pageSize,
      offset,
    });
    const batch = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    all = all.concat(batch);
    const truncated = res?.meta?.truncated;
    if (!truncated || batch.length === 0) break;
    offset += batch.length;
  }
  return all;
}

export async function fetchAllRefundsForRange(apiService, range) {
  const pageSize = 2000;
  let offset = 0;
  let all = [];

  for (;;) {
    const res = await apiService.getRefunds({
      start_date: range.start_date,
      end_date: range.end_date,
      limit: pageSize,
      offset,
    });
    const batch = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    all = all.concat(batch);
    const truncated = res?.meta?.truncated;
    if (!truncated || batch.length === 0) break;
    offset += batch.length;
  }
  return all;
}

export function getPeriodComparisonLabels(timePeriod, chartLocale, t) {
  const now = new Date();
  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i, 1).toLocaleDateString(chartLocale, { month: 'short' })
  );

  switch (timePeriod) {
    case TIME_PERIODS.THIS_YEAR:
      return { currentLabel: String(now.getFullYear()), previousLabel: String(now.getFullYear() - 1) };
    case TIME_PERIODS.LAST_YEAR:
      return {
        currentLabel: String(now.getFullYear() - 1),
        previousLabel: String(now.getFullYear() - 2),
      };
    case TIME_PERIODS.THIS_MONTH:
      return {
        currentLabel: monthNames[now.getMonth()],
        previousLabel: monthNames[now.getMonth() === 0 ? 11 : now.getMonth() - 1],
      };
    case TIME_PERIODS.LAST_MONTH: {
      const lm = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const pm = lm === 0 ? 11 : lm - 1;
      return { currentLabel: monthNames[lm], previousLabel: monthNames[pm] };
    }
    default:
      return {
        currentLabel: t?.('common.thisWeek') || 'This week',
        previousLabel: t?.('common.lastWeek') || 'Last week',
      };
  }
}
